import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";

import { useDemo } from "@/entities/demo";
import { useResetDemo } from "@/features/demo-actions";
import { classify, useMode } from "@/shared/api";
import { ROUTES } from "@/shared/config";
import { formatMoney, formatPercent } from "@/shared/lib";
import { Button, Modal, RollingNumber, useToast } from "@/shared/ui";
import { ScreenSkeleton, ScreenState } from "@/widgets/screen-state";
import { TopBar } from "@/widgets/top-bar";

/**
 * Кошелёк в демо-режиме (доработки §3).
 *
 * Пополнения и вывода здесь нет и быть не может: §3.1 запрещает переводить
 * демо-средства в реальный баланс и выводить их. Вместо двух кнопок — сброс
 * счёта, единственная операция, которая в песочнице имеет смысл.
 *
 * Сброс закрыт подтверждением, как того и требует §3.1: он закрывает все
 * позиции разом, и случайное касание стоило бы человеку всего, что он успел
 * посмотреть.
 */
export function DemoWalletPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { setMode } = useMode();
  const [confirming, setConfirming] = useState(false);
  const { data, isPending, isError, refetch } = useDemo("30d");
  const reset = useResetDemo();

  if (isPending || isError) {
    return (
      <div className="flex flex-col gap-3 px-5 py-3">
        <TopBar title={t("demo.wallet.title")} subtitle={t("demo.wallet.subtitle")} />
        {isError ? (
          <ScreenState
            tone="loss"
            icon="warningFill"
            title={t("states.error.title")}
            body={t("states.error.body")}
            action={{ label: t("states.error.action"), onClick: () => void refetch() }}
          />
        ) : (
          <ScreenSkeleton blocks={[160, 120, 100]} />
        )}
      </div>
    );
  }

  const account = data.account;

  const confirmReset = () => {
    reset.mutate(undefined, {
      onSuccess: (result) => {
        setConfirming(false);
        toast.show({
          tone: "profit",
          title: t("demo.reset.done"),
          meta: formatMoney(result.demo_balance, { suffix: "USDT" }),
        });
      },
      onError: (error) => {
        setConfirming(false);
        // Отказ по политике частоты — это `conflict`: сообщение приходит с
        // сервера вместе с датой, когда сброс снова станет доступен, и текст
        // берётся оттуда, а не сочиняется на клиенте.
        const reaction = classify(error);
        toast.show({
          tone: "loss",
          title:
            reaction.kind === "conflict" ? reaction.message : t("states.error.title"),
        });
      },
    });
  };

  return (
    <div className="stagger-children flex flex-col gap-3 px-5 py-3">
      <TopBar title={t("demo.wallet.title")} subtitle={t("demo.wallet.subtitle")} />

      <section
        className="flex flex-col gap-1 rounded-3xl p-5"
        style={{ backgroundImage: "var(--gradient-balance)" }}
      >
        <span className="text-caption font-medium text-text-secondary">
          {t("demo.balance")}
        </span>
        <RollingNumber
          value={formatMoney(account.demo_balance, { suffix: "USDT" })}
          className="text-display font-extrabold text-text-primary"
        />
        <span className="text-caption font-normal text-text-secondary">
          {t("demo.startBalance")} {formatMoney(account.initial_balance, { suffix: "USDT" })}
        </span>
      </section>

      <dl className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1 rounded-2xl border border-border-subtle bg-background-elevated p-4">
          <dt className="text-caption font-medium text-text-secondary">
            {t("demo.invested")}
          </dt>
          <dd className="text-h3 font-semibold text-text-primary">
            {formatMoney(account.invested, { suffix: "USDT" })}
          </dd>
        </div>
        <div className="flex flex-col gap-1 rounded-2xl border border-border-subtle bg-background-elevated p-4">
          <dt className="text-caption font-medium text-text-secondary">
            {t("demo.earned")}
          </dt>
          <dd className="text-h3 font-semibold text-text-primary">
            {formatMoney(account.earned_total, { sign: "always", suffix: "USDT" })}
            <span className="ml-1 text-caption font-normal text-text-secondary">
              {formatPercent(account.earned_pct, { sign: "always" })}
            </span>
          </dd>
        </div>
      </dl>

      <div className="flex flex-col gap-2">
        <Button
          size="cta"
          block
          onClick={() => {
            setMode("real");
            void navigate(ROUTES.home);
          }}
        >
          {t("demo.toReal")}
        </Button>
        <Button
          size="cta"
          variant="secondary"
          block
          disabled={!account.can_reset || reset.isPending}
          onClick={() => setConfirming(true)}
        >
          {t("demo.reset.action")}
        </Button>
      </div>

      <Link
        to={ROUTES.transactions}
        className="py-2 text-center text-caption font-semibold text-action-primary"
      >
        {t("demo.wallet.history")} →
      </Link>

      {confirming ? (
        <Modal
          tone="danger"
          icon="warningFill"
          title={t("demo.reset.confirm.title")}
          body={t("demo.reset.confirm.body")}
          onClose={() => setConfirming(false)}
          footer={
            <div className="flex flex-col gap-2">
              <Button
                size="cta"
                variant="danger"
                block
                loading={reset.isPending}
                onClick={confirmReset}
              >
                {t("demo.reset.confirm.action")}
              </Button>
              <Button
                size="cta"
                variant="ghost"
                block
                onClick={() => setConfirming(false)}
              >
                {t("common.cancel")}
              </Button>
            </div>
          }
        />
      ) : null}
    </div>
  );
}
